import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { formatInTz } from '@root/lib/date-utils';
import { buildShiftSetSlots } from '@components/TutorApp/EditShift/shift-set-utils';
import * as ShiftSetActions from '@actions/tutorShiftSet';
import type {
  AddShiftSetModalContainerProps,
  ShiftSet,
  DuplicateOption,
} from './add-shift-set-modal.types';

interface RootState {
  tutorShiftSet: {
    tutorShiftSets: ShiftSet[];
    shiftSetIdToDuplicate: string;
  };
}

export const useAddShiftSetModalService = (
  props: AddShiftSetModalContainerProps,
) => {
  const { hideModal } = props;
  const dispatch = useDispatch();

  // Read from Redux state
  const shiftSets = useSelector(
    (state: RootState) => state.tutorShiftSet.tutorShiftSets || [],
  );
  const selectedSetId = useSelector(
    (state: RootState) => state.tutorShiftSet.shiftSetIdToDuplicate || '0',
  );

  // Build duplicate options
  const duplicateOptions: DuplicateOption[] = useMemo(() => {
    const options = shiftSets.map((ss) => {
      const durationStart = formatInTz(
        ss.duration_start,
        'Asia/Manila',
        'YYYY-MM-DD',
      );
      const durationEnd = formatInTz(
        ss.duration_end,
        'Asia/Manila',
        'YYYY-MM-DD',
      );
      return {
        content: 'Shift Set: ' + durationStart + ' - ' + durationEnd,
        id: ss.id,
        value: ss.id.toString(),
      };
    });
    options.push({
      content: 'Create without duplication',
      value: '0',
      id: 0,
    });
    return options;
  }, [shiftSets]);

  const handleSetIdChange = useCallback(
    (value: string) => {
      dispatch(ShiftSetActions.updateShiftSetIdToDuplicate(value));
    },
    [dispatch],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      let newShiftSetShifts: number[] = [];
      const normalizedSetId = selectedSetId ? selectedSetId.toString() : '';

      if (normalizedSetId && normalizedSetId !== '0') {
        const shiftSet = shiftSets.find(
          (ss) => ss.id?.toString() === normalizedSetId,
        );
        if (shiftSet) {
          newShiftSetShifts = buildShiftSetSlots(
            shiftSet.shifts,
            newShiftSetShifts,
          );
        }
      }

      dispatch(ShiftSetActions.initiateNewShiftSet(newShiftSetShifts));
      hideModal();
    },
    [selectedSetId, shiftSets, dispatch, hideModal],
  );

  return {
    duplicateOptions,
    selectedSetId,
    message: props.message,
    messageType: props.messageType,
    onSetIdChange: handleSetIdChange,
    onSubmit: handleSubmit,
    onClose: hideModal,
  };
};
